import "./tailwind.css";
import "./global.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/grid";
import "@qws-org/ui-kit/styles.css";

import { Title, UIKitProvider } from "@qws-org/ui-kit";
import { lazy, type ReactNode, Suspense } from "react";
import type {
  LoaderFunction,
  MetaFunction,
  MiddlewareFunction,
} from "react-router";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useNavigation,
  useRouteLoaderData,
} from "react-router";
import { parseURL } from "ufo";
import Cookies from "universal-cookie";

import { ClientContextProvider } from "~/contexts/client-context/provider";
import { PluginContextProvider } from "~/contexts/plugin-context";
import { getGtm } from "~/core";
import { getPageLayoutService } from "~/core/services/get-page-layout.service";
import type { HreflangAlternate } from "~/core/services/hreflang-map.service";
import {
  getHreflangMap,
  lookupAlternates,
} from "~/core/services/hreflang-map.service";
import {
  getPageDesignSystem,
  stripDesignSystemMeta,
} from "~/core/services/page-design-system.service";
import { getPageLayoutV2 } from "~/core/services/page-layout.service";
import { getLayoutTranslations } from "~/core/services/static-translations.service";
import { SiteSdk } from "~/core/site-sdk";
import {
  isHiddenLayout,
  isLayoutV2,
  isSecondaryLayout,
} from "~/core/utils/layout-guards.utils";
import {
  domainsSettings,
  PageInfoProvider,
} from "~/features-deprecated/banner/usePageInfoContext";
import {
  affilateMiddleware,
  languageRedirectMiddleware,
  pageRedirectMiddleware,
} from "~/middlewares";
import { rootClientLoader } from "~/root.client-loader";
import {
  affiliateContext,
  authDataContext,
  authFeatureContext,
  availableLanguagesContext,
  cfProxyDataContext,
  featureFlagsContext,
  isBotContext,
  isSyntheticAuditContext,
  languageContext,
  redirectsContext,
  sdkContext,
  settingsContext,
  timingContext,
} from "~/server/get-load-context";
import { sanitizeLayoutCmsHtml } from "~/server/sanitize-cms-html.server";
import { nowMs } from "~/server/server-timing";
import type { LcpImageHint } from "~/shared/types/lcp-image";
import { createOverrideThemeValues } from "~/shared/utils/create-override-theme-values";
import { createTheme } from "~/shared/utils/create-theme";
import { createLinkComponent } from "~/shared/utils/createLinkComponent";
import { DirectusProvider } from "~/shared/utils/directus/directus.provider";
import { FlagsClient } from "~/shared/utils/feature-flags/core";
import { GtmProvider } from "~/shared/utils/gtm-sdk/provider";
import { createGtmInstance } from "~/shared/utils/gtm-sdk/provider/createGtmInstance";
import { DeferredGtmLoader } from "~/shared/utils/gtm-sdk/provider/DeferredGtmLoader";
import { AnjouanLicense } from "~/widgets/anjouan-license";
import { AppLayoutWidget } from "~/widgets/app-layout";
import { AuthModalsProvider } from "~/widgets/auth-modals";

const NotificationConstructor = lazy(() =>
  import("~/widgets/notifications-constructor").then((module) => ({
    default: module.NotificationConstructor,
  })),
);

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);
const ROUTE_HIDDEN_LAYOUT = {
  __typename: "hidden_layout",
  id: "route-hidden-layout",
} as const;
type RouteLayoutHandle = {
  layout?: "hidden";
};
const CRITICAL_LAYOUT_CSS = [
  "*,::before,::after{box-sizing:border-box;border-width:0;border-style:solid;border-color:#e5e7eb}html{line-height:1.5;-webkit-text-size-adjust:100%;tab-size:4}body{margin:0}h1,h2,h3,h4,h5,h6,p,ul,ol,figure,blockquote,dl,dd{margin:0}ul,ol,menu{list-style:none;padding:0}button,input,optgroup,select,textarea{font:inherit;margin:0;padding:0}button,[role=button]{cursor:pointer}img,svg,video,canvas,audio,iframe,embed,object{display:block;vertical-align:middle}img,video{max-width:100%;height:auto}",
  ".hidden{display:none}.block{display:block}.flex{display:flex}.inline-flex{display:inline-flex}.grid{display:grid}",
  ".hidden.md\\:flex,.hidden.md\\:block,.hidden.lg\\:flex,.hidden.lg\\:block{display:none}",
  ".relative{position:relative}.absolute{position:absolute}.sticky{position:sticky}.inset-0{inset:0}.-z-10{z-index:-10}.z-10{z-index:10}",
  ".top-\\[var\\(--mPositionTop\\)\\]{top:var(--mPositionTop)}.left-\\[var\\(--mPositionLeft\\)\\]{left:var(--mPositionLeft)}.right-\\[var\\(--mPositionRight\\)\\]{right:var(--mPositionRight)}.z-\\[var\\(--mPositionZ\\)\\]{z-index:var(--mPositionZ)}",
  ".w-full{width:100%}.h-full{height:100%}.min-h-screen{min-height:100vh}.flex-1{flex:1 1 0%}.basis-0{flex-basis:0}.w-\\[var\\(--width\\)\\]{width:var(--width)}.h-\\[var\\(--height\\)\\]{height:var(--height)}.max-w-\\[var\\(--maxWidth\\)\\]{max-width:var(--maxWidth)}.max-h-\\[var\\(--maxHeight\\)\\]{max-height:var(--maxHeight)}.min-w-\\[var\\(--minWidth\\)\\]{min-width:var(--minWidth)}.min-h-\\[var\\(--minHeight\\)\\]{min-height:var(--minHeight)}",
  ".pt-\\[var\\(--m-pt\\)\\]{padding-top:var(--m-pt)}.pb-\\[var\\(--m-pb\\)\\]{padding-bottom:var(--m-pb)}.pr-\\[var\\(--m-pr\\)\\]{padding-right:var(--m-pr)}.pl-\\[var\\(--m-pl\\)\\]{padding-left:var(--m-pl)}",
  ".mt-\\[var\\(--m-mt\\)\\]{margin-top:var(--m-mt)}.mb-\\[var\\(--m-mb\\)\\]{margin-bottom:var(--m-mb)}.mr-\\[var\\(--m-mr\\)\\]{margin-right:var(--m-mr)}.ml-\\[var\\(--m-ml\\)\\]{margin-left:var(--m-ml)}",
  ".gap-\\[var\\(--gap\\)\\]{gap:var(--gap)}.items-center{align-items:center}.items-end{align-items:flex-end}.items-stretch{align-items:stretch}.justify-between{justify-content:space-between}.justify-center{justify-content:center}.justify-end{justify-content:flex-end}.justify-normal{justify-content:normal}.flex-col{flex-direction:column}.flex-row{flex-direction:row}",
  ".p-0{padding:0}.mx-auto{margin-left:auto;margin-right:auto}.border{border-width:1px}.border-0{border-width:0}.overflow-hidden{overflow:hidden}.object-cover{object-fit:cover}.object-center{object-position:center}.text-center{text-align:center}.uppercase{text-transform:uppercase}.whitespace-nowrap{white-space:nowrap}.list-none{list-style-type:none}.\\[font-size\\:var\\(--fs\\)\\]{font-size:var(--fs)}.\\[font-weight\\:var\\(--fw\\)\\]{font-weight:var(--fw)}.\\[line-height\\:var\\(--lh\\)\\]{line-height:var(--lh)}",
  ".\\[\\&_img\\]\\:w-full img{width:100%}.\\[\\&_img\\]\\:h-full img{height:100%}.\\[\\&_img\\]\\:object-cover img{object-fit:cover}",
  ".swiper{position:relative;overflow:hidden;display:block;list-style:none;padding:0;z-index:1}.swiper-wrapper{position:relative;width:100%;height:100%;z-index:1;display:flex;box-sizing:content-box}.swiper-slide{flex-shrink:0;width:100%;height:100%;position:relative;display:block}",
  "@media(min-width:768px){.md\\:hidden{display:none}.md\\:block,.hidden.md\\:block{display:block}.md\\:flex,.hidden.md\\:flex{display:flex}.md\\:inline-flex{display:inline-flex}.md\\:grid{display:grid}.md\\:w-\\[var\\(--mdWidth\\)\\]{width:var(--mdWidth)}.md\\:h-\\[var\\(--mdHeight\\)\\]{height:var(--mdHeight)}.md\\:max-w-\\[var\\(--mdMaxWidth\\)\\]{max-width:var(--mdMaxWidth)}.md\\:min-w-\\[var\\(--mdMinWidth\\)\\]{min-width:var(--mdMinWidth)}.md\\:pt-\\[var\\(--md-pt\\)\\]{padding-top:var(--md-pt)}.md\\:pb-\\[var\\(--md-pb\\)\\]{padding-bottom:var(--md-pb)}.md\\:pr-\\[var\\(--md-pr\\)\\]{padding-right:var(--md-pr)}.md\\:pl-\\[var\\(--md-pl\\)\\]{padding-left:var(--md-pl)}.md\\:mt-\\[var\\(--md-mt\\)\\]{margin-top:var(--md-mt)}.md\\:gap-\\[var\\(--md-gap\\)\\]{gap:var(--md-gap)}.md\\:justify-normal{justify-content:normal}.md\\:justify-start{justify-content:flex-start}.md\\:text-left{text-align:left}.md\\:\\[font-size\\:var\\(--md-fs\\)\\]{font-size:var(--md-fs)}.md\\:\\[font-weight\\:var\\(--md-fw\\)\\]{font-weight:var(--md-fw)}.md\\:\\[line-height\\:var\\(--md-lh\\)\\]{line-height:var(--md-lh)}}",
  "@media(min-width:1024px){.lg\\:hidden{display:none}.lg\\:block,.hidden.lg\\:block{display:block}.lg\\:flex,.hidden.lg\\:flex{display:flex}.lg\\:inline-flex{display:inline-flex}.lg\\:grid{display:grid}.lg\\:w-\\[var\\(--dWidth\\)\\]{width:var(--dWidth)}.lg\\:h-\\[var\\(--dHeight\\)\\]{height:var(--dHeight)}.lg\\:max-w-\\[var\\(--dMaxWidth\\)\\]{max-width:var(--dMaxWidth)}.lg\\:min-h-\\[var\\(--dMinHeight\\)\\]{min-height:var(--dMinHeight)}.lg\\:pt-\\[var\\(--d-pt\\)\\]{padding-top:var(--d-pt)}.lg\\:pb-\\[var\\(--d-pb\\)\\]{padding-bottom:var(--d-pb)}.lg\\:pr-\\[var\\(--d-pr\\)\\]{padding-right:var(--d-pr)}.lg\\:pl-\\[var\\(--d-pl\\)\\]{padding-left:var(--d-pl)}.lg\\:pt-\\[var\\(--lg-pt\\)\\]{padding-top:var(--lg-pt)}.lg\\:pb-\\[var\\(--lg-pb\\)\\]{padding-bottom:var(--lg-pb)}.lg\\:pr-\\[var\\(--lg-pr\\)\\]{padding-right:var(--lg-pr)}.lg\\:pl-\\[var\\(--lg-pl\\)\\]{padding-left:var(--lg-pl)}.lg\\:mr-\\[var\\(--lg-mr\\)\\]{margin-right:var(--lg-mr)}.lg\\:gap-\\[var\\(--d-gap\\)\\]{gap:var(--d-gap)}.lg\\:justify-between{justify-content:space-between}}",
].join("");

// eslint-disable-next-line react-refresh/only-export-components
export const middleware: Array<MiddlewareFunction> = [
  languageRedirectMiddleware,
  affilateMiddleware,
  pageRedirectMiddleware,
];

function getConfiguredSelfHost(
  baseUrl: string | null,
  fallbackHost: string,
): string {
  if (!baseUrl) return fallbackHost;

  try {
    return new URL(baseUrl).host;
  } catch (error) {
    throw new Error(`Invalid settings_seo.base_url: ${baseUrl}`, {
      cause: error,
    });
  }
}

export const loader = (async ({ request, context }) => {
  const url = new URL(request.url);
  const featureFlags = context.get(featureFlagsContext);
  const settings = context.get(settingsContext);
  const sdk = context.get(sdkContext);
  const affiliate = context.get(affiliateContext);
  const { playerID, loginToken, authorizationToken } =
    context.get(authDataContext);
  const authFeature = context.get(authFeatureContext);
  const cfProxyData = context.get(cfProxyDataContext);
  const availableLanguages = context.get(availableLanguagesContext);
  const redirectForClient = context.get(redirectsContext);
  const isBot = context.get(isBotContext) ?? false;
  const isSyntheticAudit = context.get(isSyntheticAuditContext) ?? false;
  const isLocalRequest = LOCAL_HOSTNAMES.has(url.hostname);
  const skipThirdPartyScripts = isBot || isSyntheticAudit || isLocalRequest;
  const domainSettings = (Object.entries(domainsSettings).find(([key]) =>
    url.host.startsWith(key),
  ) ?? [])[1];

  const timing = context.get(timingContext) ?? {};
  const tCmsStart = nowMs();
  const [gtm, translations, legacyPageLayout, pageLayoutV2, pageDesignSystem] =
    await Promise.all([
      sdk.request(getGtm()),
      sdk.request(getLayoutTranslations()),
      sdk.request(getPageLayoutService()),
      sdk.request(getPageLayoutV2(url)),
      sdk.request(getPageDesignSystem(url)),
    ]);
  timing.cms = (timing.cms ?? 0) + (nowMs() - tCmsStart);

  const { host, protocol } = parseURL(request.url);
  const language = context.get(languageContext);
  const layout = sanitizeLayoutCmsHtml(settings.app.layout?.[0]?.item ?? null);
  const sanitizedPageLayout = sanitizeLayoutCmsHtml(pageLayoutV2);

  // Preload hreflang map from sitemap (cached by uuid).
  // - CMS_PUBLIC_PATH points at the Directus public origin (the CMS host),
  //   not the site host — site has no /assets/ route.
  // - selfHost is derived from CMS-side base_url so request-host aliases
  //   (www vs apex, etc.) don't make the host filter discard valid records.
  const sitemapUuid =
    typeof settings?.settings_seo?.sitemap?.id === "string"
      ? settings.settings_seo.sitemap.id
      : null;
  const cmsPublicPath = process.env.CMS_PUBLIC_PATH ?? `${protocol}//${host}`;
  const baseUrl: string | null =
    typeof settings?.settings_seo?.base_url === "string"
      ? settings.settings_seo.base_url
      : null;
  const selfHost = getConfiguredSelfHost(baseUrl, url.host);
  const hreflangMap = await getHreflangMap({
    cmsPublicPath,
    sitemapUuid,
    selfHost,
  });
  const currentPath = new URL(request.url).pathname;
  const hreflangAlternates: Array<HreflangAlternate> = lookupAlternates(
    hreflangMap,
    currentPath,
  );

  return {
    domainSettings,
    pageUrl: url,
    featureFlags: {
      isNavigationEnabled: featureFlags.getItem("headerNavigationLinks")
        .enabled,
      isLanguageSelectorEnabled: featureFlags.getItem("headerLanguageSelector")
        .enabled,
      isCreateButtonEnabled: featureFlags.getItem("headerCreateButton").enabled,
      isFooterNavigationLinkEnabled: featureFlags.getItem(
        "footerNavigationLinks",
      ).enabled,
      isFooterMenuConstructorEnabled: featureFlags.getItem(
        "footerMenuConstructor",
      ).enabled,
      isRegistrationFeatureEnabled: featureFlags.getItem("registration-feature")
        .enabled,
      isLayoutV2Enabled: featureFlags.getItem("layouts_v2").enabled,
      isPreDepBonusEnabled: featureFlags.getItem("bonusBeforeFirstDep").enabled,
      isGtmProxyEnabled: featureFlags.getItem("gtm-proxy").enabled,
    },
    affiliate,
    gtmId: skipThirdPartyScripts ? null : gtm?.container_identifier,
    metricsService: skipThirdPartyScripts ? null : gtm?.metrics_service,
    translations,
    header: { ...legacyPageLayout.header },
    cmsPath: `${protocol}//${host}`,
    fontSize: stripDesignSystemMeta(settings.app.font_size),
    fontFamily: stripDesignSystemMeta(settings.app.font_family),
    fontWeight: stripDesignSystemMeta(settings.app.font_weight),
    lineHeight: stripDesignSystemMeta(settings.app.line_height),
    colors: stripDesignSystemMeta(settings.app.colors),
    borderRadius: stripDesignSystemMeta(settings.app.border_radius),
    authFeatures: authFeature,
    availableLanguages,
    flags: featureFlags.getSerializedFlags(),
    authorizationToken,
    loginToken,
    playerID,
    footer: legacyPageLayout.footer,
    cfProxyData,
    favicon: settings.app.favicon,
    languages: settings.languages,
    homePageSlug: settings.app.home?.slug,
    hostname: new URL(request.url).hostname,
    layout,
    pageLayoutV2: sanitizedPageLayout,
    pageDesignSystem,
    pluginIntegrations: {
      anjouanLicense:
        isBot || isSyntheticAudit ? null : settings?.anjouan_licensing,
    },
    redirectForClient,
    language,
    hreflangAlternates,
    isBot,
    skipThirdPartyScripts,
  };
}) satisfies LoaderFunction;
export const clientLoader = rootClientLoader;

export function Layout({ children }: { children: ReactNode }): ReactNode {
  const data = useRouteLoaderData<typeof loader>("root");

  const matches = useMatches();
  const lcpImage =
    matches.reduce<LcpImageHint | null>((acc, match) => {
      if (acc) return acc;
      const matchData = match.data as { lcpImage?: LcpImageHint | null } | null;
      return matchData?.lcpImage ?? null;
    }, null) ?? null;
  const lcpImagePreloads =
    lcpImage?.preloads && lcpImage.preloads.length > 0
      ? lcpImage.preloads
      : lcpImage
        ? [lcpImage]
        : [];
  const language = data?.language ?? "";
  const gtmId = data?.gtmId;
  const metricsService = data?.metricsService;
  const isGtmProxy = data?.featureFlags?.isGtmProxyEnabled ?? false;
  const skipThirdPartyScripts = data?.skipThirdPartyScripts ?? true;

  const siteSdk = new SiteSdk();
  const hasHiddenRouteLayout = matches.some(
    (match) =>
      (match.handle as RouteLayoutHandle | undefined)?.layout === "hidden",
  );
  const pageLayoutOverride = data?.pageLayoutV2;
  const layoutData = hasHiddenRouteLayout
    ? ROUTE_HIDDEN_LAYOUT
    : pageLayoutOverride &&
        (isLayoutV2(pageLayoutOverride) ||
          isSecondaryLayout(pageLayoutOverride) ||
          isHiddenLayout(pageLayoutOverride))
      ? pageLayoutOverride
      : data?.layout;

  const pageDesignSystem = data?.pageDesignSystem;
  const anjouanLicensingData = data?.pluginIntegrations?.anjouanLicense ?? null;
  const overrideThemeInput = createOverrideThemeValues({
    currentThemeEntry: {
      colors: data?.colors ?? {},
      fontWeight: data?.fontWeight ?? {},
      fontSize: data?.fontSize ?? {},
      fontFamily: data?.fontFamily ?? {},
      lineHeight: data?.lineHeight ?? {},
      borderRadius: data?.borderRadius ?? {},
    },
    newThemeEntry: {
      colors: pageDesignSystem?.colors ?? {},
      fontWeight: pageDesignSystem?.font_weight ?? {},
      fontSize: pageDesignSystem?.font_size ?? {},
      lineHeight: pageDesignSystem?.line_height ?? {},
      borderRadius: pageDesignSystem?.border_radius ?? {},
    },
  });

  const theme = createTheme(language, overrideThemeInput);
  const routeContent = (
    <>
      {children}
      <Suspense fallback={null}>
        <NotificationConstructor />
      </Suspense>
    </>
  );

  return (
    <PluginContextProvider>
      <html lang={language}>
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="new-version" content="1.7.1" />
          <style>{CRITICAL_LAYOUT_CSS}</style>
          <Meta />
          {lcpImagePreloads.map((preload) => (
            <link
              key={`${preload.media ?? "default"}-${preload.url}`}
              rel="preload"
              as="image"
              href={preload.url}
              imageSrcSet={preload.srcSet}
              imageSizes={preload.sizes}
              type={preload.type}
              media={preload.media}
              fetchPriority="high"
            />
          ))}
          {data?.fontFamily?.main_name && data?.fontFamily?.main_url && (
            <>
              <link
                rel="preload"
                as="style"
                href={data.fontFamily.main_url}
                crossOrigin="anonymous"
              />
              <link
                rel="stylesheet"
                href={data.fontFamily.main_url}
                media="print"
                data-async-font=""
              />
              <noscript>
                <link rel="stylesheet" href={data.fontFamily.main_url} />
              </noscript>
            </>
          )}
          {data?.fontFamily?.additional_name &&
            data?.fontFamily?.additional_url && (
              <>
                <link
                  rel="preload"
                  as="style"
                  href={data.fontFamily.additional_url}
                  crossOrigin="anonymous"
                />
                <link
                  rel="stylesheet"
                  href={data.fontFamily.additional_url}
                  media="print"
                  data-async-font=""
                />
                <noscript>
                  <link
                    rel="stylesheet"
                    href={data.fontFamily.additional_url}
                  />
                </noscript>
              </>
            )}
          {data?.fontFamily?.accent_name && data?.fontFamily?.accent_url && (
            <>
              <link
                rel="preload"
                as="style"
                href={data.fontFamily.accent_url}
                crossOrigin="anonymous"
              />
              <link
                rel="stylesheet"
                href={data.fontFamily.accent_url}
                media="print"
                data-async-font=""
              />
              <noscript>
                <link rel="stylesheet" href={data.fontFamily.accent_url} />
              </noscript>
            </>
          )}
          {(data?.fontFamily?.main_url ??
            data?.fontFamily?.additional_url ??
            data?.fontFamily?.accent_url) && (
            <script
              suppressHydrationWarning
              dangerouslySetInnerHTML={{
                __html: `(function(){function s(){document.querySelectorAll('link[data-async-font]').forEach(function(l){l.media='all'})}if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',s)}else{s()}})();`,
              }}
            />
          )}
          {data?.favicon?.id && (
            <link rel="icon" href={`/cms-assets/${data.favicon.id}/`} />
          )}
          <Links />
        </head>
        <body>
          {/* <!-- Google Tag Manager (noscript) --> */}
          {!skipThirdPartyScripts && gtmId && (
            <noscript>
              <iframe
                src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
                height="0"
                width="0"
                style={{ display: "none", visibility: "hidden" }}
              />
            </noscript>
          )}

          {/* <!-- End Google Tag Manager (noscript) --> */}
          <ClientContextProvider
            value={
              data
                ? {
                    siteSdk,
                    authToken: data.authorizationToken,
                    loginToken: data.loginToken,
                    affiliate: data.affiliate,
                    featureFlags: new FlagsClient({
                      initialValues: data.flags,
                    }),
                    cookies: new Cookies(),
                    breadcrumbsStaticTranslations:
                      data.translations.static_translations?.breadcrumbs?.[0] ??
                      null,
                    playerID: data.playerID,
                    authFeatures: data.authFeatures,
                    cfProxyData: data.cfProxyData,
                    language,
                    availableLanguages: data.availableLanguages,
                    languages: data.languages,
                    hreflangAlternates: data.hreflangAlternates,
                    isNewUser: false,
                    anjouanLicensingData,
                  }
                : null
            }
          >
            <UIKitProvider
              value={{
                components: {
                  Link: createLinkComponent(language),
                },
                theme,
              }}
            >
              {data ? (
                <GtmProvider sdk={createGtmInstance()}>
                  {!skipThirdPartyScripts && (
                    <DeferredGtmLoader
                      gtmId={gtmId ?? undefined}
                      isGtmProxy={isGtmProxy}
                      metricsService={metricsService ?? undefined}
                    />
                  )}
                  <DirectusProvider url={data.cmsPath}>
                    <AuthModalsProvider
                      siteSdk={siteSdk}
                      playerId={data.playerID}
                    >
                      <AppLayoutWidget layoutData={layoutData}>
                        {routeContent}
                      </AppLayoutWidget>
                      <AnjouanLicense />
                    </AuthModalsProvider>
                  </DirectusProvider>
                </GtmProvider>
              ) : (
                children
              )}
            </UIKitProvider>
          </ClientContextProvider>
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    </PluginContextProvider>
  );
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const result: ReturnType<MetaFunction<typeof loader>> = [];

  // RR7 SSR strips custom headers on thrown Response; noindex отдаём через meta-tag.
  const hasRouteError = matches.some((match) => match.error != null);
  if (hasRouteError) {
    result.push({ name: "robots", content: "noindex, nofollow" });
  }

  if (data?.hostname === "xingyunpoker.com") {
    result.push({ title: "LuckyPoker 休闲扑克玩家的天堂" });
  }

  return result;
};

function App(): ReactNode {
  const loaderData = useLoaderData<typeof loader>();
  const { pageUrl, domainSettings } = loaderData;
  const navigationState = useNavigation();
  return (
    <>
      {["loading", "submiting"].includes(navigationState.state) && (
        <div
          className="fixed fixedtop-0 left-0 w-full h-1 bg-blue-500 "
          style={{ zIndex: 99 }}
        />
      )}
      <PageInfoProvider url={pageUrl} domainSettings={domainSettings}>
        <Outlet />
      </PageInfoProvider>
    </>
  );
}
export function ErrorBoundary({ error }: { error: unknown }): ReactNode {
  if (isRouteErrorResponse(error)) {
    return (
      <>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </>
    );
  }

  return (
    <Title>
      Something went wrong, try clearing your browser cache and reloading the
      page.
    </Title>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default App;
